import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  TextField,
  Box,
  Typography
} from '@mui/material';
import CustomButtonContained from 'src/components/CustomButton/CustomButtonContained';

export interface PetFeeBreakdown {
  licenseFee: number;
  stateRegistration: number;
  pilotClinic: number;
  animalPopulationControl?: number;
  subtotal: number;
  lateFee: number;
  total: number;
  lateFeesWaived?: boolean;
}

interface PaymentTypeModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (paymentType: string, checkNumber?: string) => void;
  totalAmount: number;
  petFeeBreakdown?: PetFeeBreakdown;
}

const PaymentTypeModal: React.FC<PaymentTypeModalProps> = ({
  open,
  onClose,
  onConfirm,
  totalAmount,
  petFeeBreakdown
}) => {
  const [paymentType, setPaymentType] = useState<string>('Credit Card');
  const [checkNumber, setCheckNumber] = useState<string>('');
  const [errors, setErrors] = useState<{ checkNumber?: string }>({});

  const handlePaymentTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentType(event.target.value);
    setCheckNumber('');
    setErrors({});
  };

  const handleConfirm = () => {
    // Validate check number if Check is selected
    if (paymentType === 'Check') {
      if (!checkNumber || checkNumber.trim() === '') {
        setErrors({ checkNumber: 'Check number is required' });
        return;
      }
      onConfirm(paymentType, checkNumber.trim());
    } else {
      onConfirm(paymentType);
    }
  };

  const handleClose = () => {
    setPaymentType('Credit Card');
    setCheckNumber('');
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          Select Payment Type
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend">Payment Method</FormLabel>
            <RadioGroup
              value={paymentType}
              onChange={handlePaymentTypeChange}
              sx={{ mt: 1 }}
            >
              <FormControlLabel
                value="Credit Card"
                control={<Radio />}
                label="Credit Card"
              />
              <FormControlLabel
                value="Check"
                control={<Radio />}
                label="Check"
              />
              <FormControlLabel
                value="Cash"
                control={<Radio />}
                label="Cash"
              />
            </RadioGroup>
          </FormControl>

          {paymentType === 'Check' && (
            <Box sx={{ mt: 3 }}>
              <TextField
                fullWidth
                label="Check Number"
                value={checkNumber}
                onChange={(e) => {
                  setCheckNumber(e.target.value);
                  if (errors.checkNumber) {
                    setErrors({});
                  }
                }}
                error={!!errors.checkNumber}
                helperText={errors.checkNumber}
                required
              />
            </Box>
          )}

          {paymentType === 'Cash' && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Please confirm that cash payment will be processed.
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            {petFeeBreakdown ? (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">License Fee</Typography>
                  <Typography variant="body2">${petFeeBreakdown.licenseFee.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">State Registration</Typography>
                  <Typography variant="body2">${petFeeBreakdown.stateRegistration.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Pilot Clinic</Typography>
                  <Typography variant="body2">${petFeeBreakdown.pilotClinic.toFixed(2)}</Typography>
                </Box>
                {petFeeBreakdown.animalPopulationControl !== undefined && petFeeBreakdown.animalPopulationControl > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Animal Population Control Fee</Typography>
                    <Typography variant="body2">${petFeeBreakdown.animalPopulationControl.toFixed(2)}</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
                  <Typography variant="body2" fontWeight={600}>Subtotal</Typography>
                  <Typography variant="body2" fontWeight={600}>${petFeeBreakdown.subtotal.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">
                    Late Fee
                    {petFeeBreakdown.lateFeesWaived ? (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        (waived)
                      </Typography>
                    ) : null}
                  </Typography>
                  <Typography variant="body2">${petFeeBreakdown.lateFee.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid', borderColor: 'divider', pt: 1, mt: 1 }}>
                  <Typography variant="body2" fontWeight={700}>Total Amount to Pay</Typography>
                  <Typography variant="h6" color="primary">${totalAmount.toFixed(2)}</Typography>
                </Box>
              </Box>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary">
                  Total Amount:
                </Typography>
                <Typography variant="h6" color="primary">
                  ${totalAmount.toFixed(2)}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <CustomButtonContained
          title="Proceed"
          onClick={handleConfirm}
        />
      </DialogActions>
    </Dialog>
  );
};

export default PaymentTypeModal;
